import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Article 6 - Omakase 禮儀大全
 * SEO 由 ArticlePage.jsx 集中管理，此組件只負責內容渲染
 */
export default function Article6() {
  return (
    <div>
      {/* 引言 */}
      <section className="mb-12">
        <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300 mb-6">
          Omakase 不僅是一頓飯，更是一場文化體驗。正確的禮儀不僅能讓您獲得更好的服務和體驗，更重要的是展現對廚師、食材和日本料理文化的尊重。本文將詳細介紹從預約到離席的每一個環節，讓您成為一位受歡迎的 Omakase 客人。
        </p>
      </section>

      {/* 預約禮儀 */}
      <section className="mb-16">
        <h2 id="reservation-etiquette" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-rose-500">
          預約 Omakase 時該注意什麼？
        </h2>
        <div className="bg-gradient-to-br from-rose-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 p-8 rounded-2xl mb-8">
          <h3 id="reservation-timing" className="text-2xl font-bold text-gray-900 dark:text-white mb-4">預約時機</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: '一般餐廳', time: '提前 1-2 週預約即可' },
              { title: '熱門餐廳', time: '提前 1-3 個月預約' },
              { title: '頂級餐廳', time: '提前 3-6 個月預約' },
            ].map(item => (
              <div key={item.title} className="bg-white dark:bg-gray-800 p-4 rounded-xl">
                <h4 className="font-bold text-rose-600 dark:text-rose-400 mb-2">{item.title}</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{item.time}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg mb-6">
          <h3 id="reservation-information" className="text-2xl font-bold text-gray-900 dark:text-white mb-4">預約時應提供的資訊</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-rose-600 dark:text-rose-400 mb-3">基本資訊</h4>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                {['姓名：提供完整姓名（中英文）', '聯絡方式：手機號碼和 Email', '用餐日期與時間：提供第一和第二志願', '人數：確認準確人數'].map(item => (
                  <li key={item} className="flex items-start"><span className="text-rose-500 mr-2 mt-1">●</span><div>{item}</div></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-rose-600 dark:text-rose-400 mb-3">特殊需求</h4>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                {['飲食限制：過敏原、宗教因素、不吃的食材', '特殊場合：生日、紀念日、求婚等', '座位偏好：吧台或包廂', '預算：某些餐廳可根據預算調整菜單'].map(item => (
                  <li key={item} className="flex items-start"><span className="text-rose-500 mr-2 mt-1">●</span><div>{item}</div></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 p-6 rounded-r-xl">
          <h3 className="font-bold text-yellow-900 dark:text-yellow-300 mb-3">⚠️ 預約注意事項</h3>
          <ul className="space-y-2 text-yellow-800 dark:text-yellow-200 text-sm">
            {['如需取消或更改，請至少提前 24-48 小時通知', '某些餐廳要求信用卡擔保或預付訂金', '遲到超過 15 分鐘可能會被取消預約', '不要預約後不出現（No-show），這會被列入黑名單'].map(item => (
              <li key={item}>• {item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* 著裝禮儀 */}
      <section className="mb-16">
        <h2 id="dress-code" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-rose-500">
          著裝禮儀
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <h3 id="appropriate-attire" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="text-3xl">✅</span>建議穿著
            </h3>
            <div className="space-y-4">
              {[
                { title: '男士', items: ['Smart Casual 或商務休閒', '襯衫（可搭配西裝外套）', '長褲（西裝褲或休閒褲）', '皮鞋或乾淨的休閒鞋'] },
                { title: '女士', items: ['洋裝或套裝', '襯衫搭配長褲或裙子', '高跟鞋或優雅的平底鞋', '適度的配飾'] },
              ].map(group => (
                <div key={group.title} className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl">
                  <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">{group.title}</h4>
                  <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">{group.items.map(i => <li key={i}>• {i}</li>)}</ul>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
            <h3 id="inappropriate-attire" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <span className="text-3xl">❌</span>避免穿著
            </h3>
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl">
              <h4 className="font-bold text-red-600 dark:text-red-400 mb-2">男女通用</h4>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                {['拖鞋、涼鞋', '短褲、短裙（過短）', '背心、無袖上衣', '運動服、球鞋', '破損或過度休閒的牛仔褲', '過度暴露的服裝', '避免使用強烈香水'].map(i => <li key={i}>• {i}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 用餐禮儀 */}
      <section className="mb-16">
        <h2 id="dining-etiquette" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-rose-500">
          用餐禮儀
        </h2>

        {/* 筷子 */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg mb-6">
          <h3 id="chopstick-etiquette" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <span className="text-3xl">🥢</span>筷子使用禮儀
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-bold text-green-600 dark:text-green-400 mb-3">✓ 正確使用</h4>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                {['不使用時平放在筷架上', '用完後整齊放回原位', '可用手吃壽司（更傳統）', '用筷子輕輕夾取食物'].map(i => (
                  <li key={i} className="flex items-start"><span className="text-green-500 mr-2">•</span><span>{i}</span></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-red-600 dark:text-red-400 mb-3">✗ 禁忌動作</h4>
              <ul className="space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                {['將筷子插在飯中（喪禮習俗）', '用筷子指人或揮舞', '用筷子在盤中翻找食物', '兩人用筷子傳遞食物（喪禮習俗）', '舔筷子或咬筷子'].map(i => (
                  <li key={i} className="flex items-start"><span className="text-red-500 mr-2">•</span><span>{i}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 醬油與山葵 */}
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg mb-6">
          <h3 id="soy-sauce-wasabi" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <span className="text-3xl">🍶</span>醬油與山葵使用
          </h3>
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl">
              <h4 className="font-bold text-green-600 dark:text-green-400 mb-3">壽司的正確沾法</h4>
              <ol className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                {[
                  '大多數壽司已經調味，不一定需要沾醬油',
                  '如需沾醬，將壽司側翻，用魚肉面輕沾醬油',
                  '避免米飯面沾醬油（會吸收過多醬油，破壞平衡）',
                  '只需輕沾一下，不要浸泡',
                ].map((step, i) => (
                  <li key={i} className="flex items-start"><span className="font-bold mr-2">{i + 1}.</span><span>{step}</span></li>
                ))}
              </ol>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl">
              <h4 className="font-bold text-yellow-600 dark:text-yellow-400 mb-3">山葵（Wasabi）使用</h4>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                {['不要將山葵混入醬油（這會破壞兩者的風味）', '廚師已在壽司中放入適量山葵', '如果您不吃山葵，預約時就應告知', '如需額外山葵，可請廚師添加'].map(i => <li key={i}>• {i}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 拍照禮儀 */}
      <section className="mb-16">
        <h2 id="photography-etiquette" className="text-3xl font-bold text-gray-900 dark:text-white mb-8 pb-4 border-b-2 border-rose-500">
          拍照禮儀
        </h2>
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl">
              <h3 className="font-bold text-green-600 dark:text-green-400 mb-3">✓ 可以做的</h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                {['拍攝自己面前的料理', '關閉閃光燈和快門聲', '快速拍攝，不影響用餐節奏', '拍攝前先徵得同意（如果不確定）'].map(i => (
                  <li key={i} className="flex items-start"><span className="text-green-500 mr-2">•</span><span>{i}</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-xl">
              <h3 className="font-bold text-red-600 dark:text-red-400 mb-3">✗ 不可以做的</h3>
              <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2">
                {['使用閃光燈', '拍攝其他客人（未經同意）', '站起來拍攝或改變角度', '讓拍照影響用餐節奏', '在禁止拍照的餐廳拍照'].map(i => (
                  <li key={i} className="flex items-start"><span className="text-red-500 mr-2">•</span><span>{i}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 結語 */}
      <section className="mb-16">
        <h2 id="conclusion" className="text-3xl font-bold text-gray-900 dark:text-white mb-6">結語</h2>
        <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 p-8 rounded-2xl">
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
            掌握正確的 Omakase 禮儀，不僅能讓您獲得更好的用餐體驗，更重要的是展現對日本料理文化、廚師和食材的尊重。這些禮儀規範並非束縛，而是幫助您更深入地理解和享受 Omakase 的文化精髓。
          </p>
          <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
            Omakase 是一場信任的旅程，也是一次文化的交流。當您以正確的禮儀和真誠的態度參與其中時，您將獲得的不僅是美食，更是一段難忘的體驗和對日本料理文化的深刻理解。
          </p>
        </div>
      </section>

      {/* 延伸閱讀 */}
      <section className="mt-16 pt-8 border-t-2 border-gray-200 dark:border-gray-700">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">延伸閱讀</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { to: '/beginners-complete-guide-to-omakase', icon: '📚', title: '初學者的 Omakase 完整指南', desc: '從零開始了解 Omakase 的基礎知識', gradient: 'from-orange-400 to-rose-500', hover: 'orange' },
            { to: '/premium-ingredients-analysis-from-tsukiji', icon: '🐟', title: '頂級食材解析', desc: '深入了解 Omakase 使用的頂級食材', gradient: 'from-blue-400 to-cyan-500', hover: 'blue' },
            { to: '/taiwan-omakase-culture-development', icon: '🇹🇼', title: '台灣 Omakase 文化', desc: '了解台灣 Omakase 的發展', gradient: 'from-green-400 to-emerald-500', hover: 'green' },
          ].map(link => (
            <Link key={link.to} to={link.to} className="group bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className={`h-48 bg-gradient-to-br ${link.gradient} flex items-center justify-center`}>
                <span className="text-6xl">{link.icon}</span>
              </div>
              <div className="p-6">
                <h3 className={`text-lg font-bold text-gray-900 dark:text-white mb-2 group-hover:text-${link.hover}-500 transition-colors`}>{link.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm">{link.desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}